import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "./Planner.css";
import ItineraryNav from "../../components/navbar/itinerary-nav/ItineraryNav";
import { ReactComponent as EditIcon } from "../../assets/edit-icon.svg";
import { ReactComponent as PlaneIcon } from "../../assets/plane-icon.svg";
import { ReactComponent as AddIcon } from "../../assets/add-icon.svg";
import { ReactComponent as SleepIcon } from "../../assets/sleep-icon.svg";
import { ReactComponent as LocationIcon } from "../../assets/location-icon.svg";
import DatePickerValue from "../../components/date-picker/DatePicker";
import TimePickerValue from "../../components/time-picker/TimePicker";
import { useSelector, useDispatch } from "react-redux";
// Importing Redux selectors and actions
import {
  selectCurrentTrip,
  setCurrentFlightId,
  setCurrentAccommodationId,
  setCurrentDailyPlanId,
  setFlights,
  setAccommodations,
  setDailyPlans,
  setCurrentTripId,
  setCurrentTrip,
  updateFlight,
  updateAccommodation,
  updateDailyPlan,
} from "../../store/tripsSlice";
import FlightInput from "../../components/flight-input/FlightInput";
import AccommodationInput from "../../components/acco-input/accoInput";
import DayPlanInput from "../../components/daily-plan-input/dailyPlanInput";

function Planner() {
  // Extract the trip ID from the route parameters
  const { trip_id } = useParams();
  const dispatch = useDispatch();

  // Select the current trip from Redux store
  const currentTrip = useSelector(selectCurrentTrip);

  // Local UI state for form visibility and messages
  const [showFlightInput, setShowFlightInput] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showAccommodationInput, setShowAccommodationInput] = useState(false);
  const [showDayPlanInput, setShowDayPlanInput] = useState(false);

  // Handle showing flight input form
  const handleAddClick = () => {
    setSuccessMessage(""); // clear previous success
    setShowFlightInput(true);
  };

  // Handle successful flight addition
  const handleFlightAddSuccess = (message) => {
    setShowFlightInput(false);
    setSuccessMessage(message);
  };

  // Handle showing accommodation input form
  const handleAddAccommodationClick = () => {
    setSuccessMessage("");
    setShowAccommodationInput(true);
  };

  // Handle successful accommodation addition
  const handleAccommodationAddSuccess = (message) => {
    setShowAccommodationInput(false);
    setSuccessMessage(message);
  };

  // Handle successful daily plan addition
  const handleDayPlanAddSuccess = (message) => {
    setShowDayPlanInput(false);
    setSuccessMessage(message);
  };

  // Handle showing day plan input form
  const handleAddDayPlanClick = () => {
    setSuccessMessage("");
    setShowDayPlanInput(true);
  };

  // Fetch trip data and dispatch to Redux store
  useEffect(() => {
    if (trip_id) {
      const fetchFlight = async () => {
        try {
          const response = await fetch(
            `https://tripmate-rbck.onrender.com/trips/${trip_id}`
          );
          if (!response.ok) throw new Error("Network response was not ok");
          const data = await response.json();

          // Populate Redux store with fetched data
          dispatch(setFlights(data.trip.flights));
          dispatch(setAccommodations(data.trip.accommodations));
          dispatch(setDailyPlans(data.trip.daily_plans));
          dispatch(setCurrentTrip(data.trip));
          dispatch(setCurrentTripId(data.trip.trip_id));

          // Dispatch individual items for updates
          data.trip.flights.forEach((flight) => dispatch(updateFlight(flight)));
          data.trip.accommodations.forEach((acc) =>
            dispatch(updateAccommodation(acc))
          );
          data.trip.daily_plans.forEach((plan) =>
            dispatch(updateDailyPlan(plan))
          );

          // Set initial current item IDs if available
          dispatch(setCurrentFlightId(data.trip.flights[0]?.flight_id || null));
          dispatch(
            setCurrentAccommodationId(
              data.trip.accommodations[0]?.accommodation_id || null
            )
          );
          dispatch(
            setCurrentDailyPlanId(
              data.trip.daily_plans[0]?.daily_plan_id || null
            )
          );
        } catch (error) {
          console.error("Error loading flight:", error);
        }
      };
      fetchFlight();
    }
  }, [trip_id, dispatch]);

  return (
    <div className="planner-page">
      <ItineraryNav />

      {/* Trip Details Section */}
      <div className="itinerary-container">
        <div className="planner-title">
          {currentTrip ? (
            <div>
              <p className="planner-heading">{currentTrip.trip_name}</p>
              <p>{currentTrip.destination}</p>
            </div>
          ) : (
            <p>No trip selected</p>
          )}
          <button className="icon-style"></button>
        </div>
      </div>

      {/* Flights Section */}
      <div className="itinerary-container">
        <div className="planner-title">
          <PlaneIcon />
          <p className="planner-heading">Flights</p>
          <button className="icon-style">
            <EditIcon />
          </button>
        </div>

        {/* Render flight details or fallback */}
        {currentTrip?.flights ? (
          Object.values(currentTrip.flights).map((flight) => (
            <div key={flight.flight_id} className="flight-detail">
              <div className="planner-label-container">
                <p className="planner-label">From</p>
                <p className="planner-label">To</p>
              </div>
              <div className="planner-label-container">
                <p className="planner-text">
                  {flight.departure_airport || "N/A"}
                </p>
                <p className="planner-text">
                  {flight.arrival_airport || "N/A"}
                </p>
              </div>

              <div className="planner-label-container">
                <p className="planner-label">Airline</p>
                <p className="planner-label">Flight No</p>
              </div>
              <div className="planner-label-container">
                <p className="planner-text">{flight.airline || "N/A"}</p>
                <p className="planner-text">{flight.flight_number || "N/A"}</p>
              </div>
            </div>
          ))
        ) : (
          <p className="planner-text">No flights available</p>
        )}

        {/* Add Flight Button and Input */}
        <div>
          <button className="add-button" onClick={handleAddClick}>
            <AddIcon />
            Add Flights
          </button>

          {showFlightInput && (
            <FlightInput
              postRoute="/add-flight"
              onSuccess={handleFlightAddSuccess}
            />
          )}
          {successMessage && (
            <p className="success-message">{successMessage}</p>
          )}
        </div>
      </div>

      {/* Accommodation Section */}
      <div className="itinerary-container">
        <div className="planner-title">
          <SleepIcon />
          <p className="planner-heading">Accommodation</p>
          <button className="icon-style">
            <EditIcon />
          </button>
        </div>

        {/* Render accommodation details or fallback */}
        {currentTrip?.accommodations ? (
          Object.values(currentTrip.accommodations).map((acco) => (
            <div key={acco.accommodation_id} className="accommodation-detail">
              <div className="planner-label-container">
                <p className="planner-label">Name</p>
                <p className="planner-label">Address</p>
              </div>
              <div className="planner-label-container">
                <p className="planner-text">{acco.name || "N/A"}</p>
                <p className="planner-text">{acco.address || "N/A"}</p>
              </div>

              <div className="planner-label-container">
                <p className="planner-label">Check in</p>
                <p className="planner-label">Check out</p>
              </div>
              <div className="planner-label-container">
                <p className="planner-text">
                  {acco.check_in_date
                    ? new Intl.DateTimeFormat("en-GB")
                        .format(new Date(acco.check_in_date))
                        .replace(/\//g, "-")
                    : "N/A"}
                </p>
                <p className="planner-text">
                  {acco.check_out_date
                    ? new Intl.DateTimeFormat("en-GB")
                        .format(new Date(acco.check_out_date))
                        .replace(/\//g, "-")
                    : "N/A"}
                </p>
              </div>
            </div>
          ))
        ) : (
          <p className="planner-text">No accommodations available</p>
        )}

        {/* Add Accommodation Button and Input */}
        <div>
          <button className="add-button" onClick={handleAddAccommodationClick}>
            <AddIcon />
            Add Accommodation
          </button>

          {showAccommodationInput && (
            <AccommodationInput
              postRoute="/add-accommodation"
              onSuccess={handleAccommodationAddSuccess}
            />
          )}
          {successMessage && (
            <p className="success-message">{successMessage}</p>
          )}
        </div>
      </div>

      {/* Day Plan Section */}
      <div className="itinerary-container">
        <div className="planner-title">
          <LocationIcon />
          <p className="planner-heading">Day Plan</p>
          <button className="icon-style">
            <EditIcon />
          </button>
        </div>

        {/* Render day plan details or fallback */}
        {currentTrip?.daily_plans ? (
          Object.values(currentTrip.daily_plans).map((plan) => (
            <div key={plan.daily_plan_id} className="day-detail">
              <div className="day-plan-label-container">
                <p className="day-plan-label">Name</p>
                <p className="day-plan-text">{plan.name || "N/A"}</p>
              </div>
              <div className="day-plan-label-container">
                <p className="day-plan-label">Info</p>
                <p className="day-plan-text">{plan.information || "N/A"}</p>
              </div>
              <div className="day-plan-label-container">
                <p className="day-plan-label">Date</p>
                <p className="day-plan-text">
                  {plan.date ? (
                    new Intl.DateTimeFormat("en-GB")
                      .format(new Date(plan.date))
                      .replace(/\//g, "-")
                  ) : (
                    <DatePickerValue />
                  )}
                </p>
              </div>
              <div className="day-plan-label-container">
                <p className="day-plan-label">Time</p>
                <p className="day-plan-text">
                  {plan.time ? (
                    plan.time.replace(/^(\d{2}):(\d{2}):\d{2}$/, "$1:$2")
                  ) : (
                    <TimePickerValue />
                  )}
                </p>
              </div>
              <div className="day-plan-label-container">
                <p className="day-plan-label">Address</p>
                <p className="day-plan-text">{plan.address || "N/A"}</p>
              </div>
            </div>
          ))
        ) : (
          <p className="day-plan-text">No daily plans available</p>
        )}

        {/* Add Day Plan Button and Input */}
        <button className="add-button" onClick={handleAddDayPlanClick}>
          <AddIcon />
          Add Plan
        </button>

        {showDayPlanInput && (
          <DayPlanInput
            postRoute="/add-day-plan"
            onSuccess={handleDayPlanAddSuccess}
          />
        )}
        {successMessage && <p className="success-message">{successMessage}</p>}
      </div>
    </div>
  );
}

export default Planner;
